import { RingModel, IMetalGroupExt, IMetalZone, IStoneGroupExt, IStoneZone } from "../../Model/RingModel";
import { RenderEngine } from "../3DController/core/RenderEngine";
import { IMetalGroup, IStoneGroup } from "../../sharedInterfaces";
import { RING_MATERIALS, LIMITED_MATERIALS, ringMaterialHelper } from "../../Model/RingMaterials";
import { MeshPhysicalMaterial } from "three";
import { ExceptionHandler } from "./ExceptionHandler";
import { ICheckoutArgument } from "../../Service/RequestService";

/*
 * Represents state of the currently active ring.
 */

export interface IUpdateMetalGroupOptions {
  forceMaterialUpdate?:boolean,
  forceSurfaceUpdate?:boolean,
  forcePurityUpdate?:boolean,
  ignoreExceptionActions?:string[]
}

interface IUpdateStoneGroupOptions {
  forceUpdate?:boolean,
}

export interface IAPIConfigurationInfo {
  ringState:IRingState,
  checkoutArgument?:ICheckoutArgument,
  serializedCheckourArgument?:string,
  price:number
}

export interface IRingState {
  ringModel:RingModel,
  fingerSize:string,
  width:number,
  thickness:number,
  stoneSize:number,
  metalGroups:IMetalGroupExt[],
  metalZones:IMetalZone[],
  stoneGroups:IStoneGroupExt[],
  stoneZones:IStoneZone[],
  engravingText:string,
  engravingFont:'Brush Script'|'Century Gothic'|'Snell Roundhand'|'Trajan'|'Dakota'
}

export class RingStateController {
  private _renderEngine:RenderEngine;
  private _exceptionHandler:ExceptionHandler;

  private _ringModel:RingModel;
  private _fingerSize:string;
  private _width:number;
  private _thickness:number;
  private _stoneSize:number;
  private _metalGroups:IMetalGroupExt[];
  private _metalZones:IMetalZone[];
  private _stoneGroups:IStoneGroupExt[];
  private _stoneZones:IStoneZone[];
  private _engravingText:string;
  private _engravingFont:'Brush Script'|'Century Gothic'|'Snell Roundhand'|'Trajan'|'Dakota';

  constructor(renderEngine:RenderEngine, initialState:IRingState) {
    this._renderEngine = renderEngine;
    this._exceptionHandler = new ExceptionHandler(this);

    this._fingerSize = initialState.fingerSize;
    this._width = initialState.width;
    this._thickness = initialState.thickness;
    this._stoneSize = initialState.stoneSize;
    this._metalGroups = initialState.metalGroups;
    this._metalZones = initialState.metalZones;
    this._stoneGroups = initialState.stoneGroups;
    this._stoneZones = initialState.stoneZones;
    this._engravingText = initialState.engravingText;
    this._engravingFont = initialState.engravingFont;
  }

  public getState = ():IRingState => {
    const ringState:IRingState = {
      ringModel: this._ringModel,
      fingerSize: this._fingerSize,
      width: this._width,
      thickness: this._thickness,
      stoneSize: this._stoneSize,
      metalGroups: this._metalGroups,
      metalZones: this._metalZones,
      stoneGroups: this._stoneGroups,
      stoneZones: this._stoneZones,
      engravingText: this._engravingText,
      engravingFont: this._engravingFont
    }

    return ringState;
  };

  private _setRingModel = async (newRingModel:RingModel):Promise<void> => {
    this._ringModel = newRingModel;
    this._metalGroups = newRingModel.metalGroups;
    this._metalZones = newRingModel.metalZones;
    this._stoneGroups = newRingModel.stoneGroups;
    this._stoneZones = newRingModel.stoneZones;


    const { _renderEngine: renderEngine } = this;
    const configurator = renderEngine.configurator;
    const SKU = newRingModel.SKU;
    const ringGroupings = configurator.config.groupings.filter(g => g.name.startsWith(`${SKU}_`)).map(g => g.id);
    const meshes = await this._renderEngine.engine.services.render.getAllMeshesOfCurrentScene()

    //console.log("_setRingSKU!", SKU);

    //FIXME: TODO: Create visibility set (in SceneGenerator) and handle switching rings based on those rather than via groupings?
    //Hide everything
    configurator.config.groupings.forEach((g) => {
      configurator.setVisible(g.id, false)
    });

    //Then show groupings belonging to current ring
    await configurator.setVisible(ringGroupings, true);

    //Enable polygonOffset for engraving material(s) to prevent Z fighting
    const engravingGroupings = configurator.config.groupings.filter(g => (g.name.includes("Engraving") || g.name.includes("Logo")));

    engravingGroupings.forEach((engravingGrouping)=> {
      engravingGrouping.object_names.forEach((el) => {
        try {
          const engravingMesh = meshes.find(mesh => mesh.name === el);
          const engravingMaterial = (engravingMesh.material as THREE.Material);

          engravingMaterial.polygonOffset = true;
          engravingMaterial.polygonOffsetFactor = -1;
          engravingMaterial.polygonOffsetUnits = -1;
        } catch {
          console.error('Mesh/ Material not found for engraving grouping!', engravingGrouping);
        };
      });
    });
    //--

    return Promise.resolve();
  }

  public switchRing = (newRing:RingModel):Promise<void> => {
    const oldMetalGroups = this._metalGroups.map(mg => mg);
    const oldStoneGroups = this._stoneGroups.map(sg => sg);

    return new Promise((resolve, reject) => {
      this._setRingModel(newRing)
      .then(() => {
        this.setWidth(this._width, false);
        this.setThickness(this._thickness);
        this.setStoneSize(this._stoneSize);

        this._metalGroups.forEach((metalGroup) => {
          try {
            const oldMetalGroup = oldMetalGroups.find(omg => omg.name === metalGroup.name);
            metalGroup.material = oldMetalGroup.material;
            metalGroup.surface = oldMetalGroup.surface;
            metalGroup.purity = oldMetalGroup.purity;
          } catch(warn) {
            console.error(`MetalGroup '${metalGroup.name}' not found on previous ring! Falling back on default material.`, metalGroup);
          };

          this.updateMetalGroup(metalGroup, { ignoreExceptionActions: ['copy', 'prevent', 'message'], forceMaterialUpdate: true, forcePurityUpdate: true, forceSurfaceUpdate: true });
        });

        this._stoneGroups.forEach((stoneGroup) => {
          try {
            const oldStoneGroup = oldStoneGroups.find(osg => osg.name === stoneGroup.name);
            stoneGroup.stone = oldStoneGroup.stone;
          } catch(warn) {
            console.warn(`StoneGroup '${stoneGroup.name}' not found on previous ring! Falling back on default stone type.`, stoneGroup);
          };

          this.updateStoneGroup(stoneGroup, { forceUpdate: true });
        });

        this.updateDisabledOptions();

        resolve();
      });
    })
  };// /_switchRing()

  public setFingerSize = (setTo:string) => {
    this._fingerSize = setTo;
  };// /_setFingerSize()

  public setWidth = (setTo:number, autoRatio:boolean):Promise<number> => {
    const { _ringModel: ringModel } = this;
    const morphOptions = ringModel.morphOptions;
    const widthOptions = morphOptions.width;
    const minWidthOption = Math.min(...widthOptions);
    const maxWidthOption = Math.max(...widthOptions);

    if (setTo < minWidthOption) {
      console.warn(`Attempted to set width to ${setTo}, but lowest available option is ${minWidthOption}`);
      setTo = minWidthOption;
    } else if (setTo > maxWidthOption) {
      console.warn(`Attempted to set width to ${setTo}, but highest available option is ${maxWidthOption}`);
      setTo = maxWidthOption;
    };

    this._width = setTo;
    this._renderEngine.updateWidthOfRing(this._width);

    if (autoRatio) {
      const thickness = this._ringModel.getIdealThickness(this._width);
      this.setThickness(thickness.ideal);
    } else {
      //Call setThickness to check if its within mixMaxThickness
      this.setThickness(this._thickness);
    };

    //We don't need to call setStoneSize here to check for sizeExceptions, since that happens in setThickness()

    return new Promise((resolve, reject) => {
      this._renderEngine.engine.services.render.getAllMeshesOfCurrentScene()
      .then((meshes:THREE.Mesh[]) => {
        meshes = meshes.filter((mesh) => (mesh.userData['ringSKU'] === ringModel.SKU));
        meshes.forEach((mesh:THREE.Mesh) => {
          const influence = this._calculateShapeKeyInfluence(widthOptions[0], widthOptions[widthOptions.length - 1], setTo);
          const targetIndex = mesh.userData.morphIndexes.width;
          if (targetIndex !== null) mesh.morphTargetInfluences[targetIndex] = influence;
        });
        resolve(this._width);
      });
    });
  };// /setWidth()

  public setThickness = async (setTo:number):Promise<number> => {
    const { _ringModel: ringModel } = this;
    const morphOptions = ringModel.morphOptions;
    const thicknessOptions = morphOptions.thickness;
    const minThicknessOption = Math.min(...thicknessOptions);
    const maxThicknessOption = Math.max(...thicknessOptions);

    const minMaxThickness = ringModel.getMinMaxThickness(this._width);

    if (setTo < minMaxThickness.min) {
      //console.log(`Attempted to set thickness to ${setTo}, but minimum allowed is ${minMaxThickness.min}`);
      const availableThicknesses = thicknessOptions.filter(o => o >= minMaxThickness.min);
      setTo = Math.min(...availableThicknesses);
    } else if (setTo > minMaxThickness.max) {
      //console.log(`Attempted to set thickness to ${setTo}, but maximum allowed is ${minMaxThickness.max}`);
      const availableThicknesses = thicknessOptions.filter(o => o <= minMaxThickness.max);
      setTo = Math.max(...availableThicknesses);
    };

    if (setTo < minThicknessOption) {
      console.warn(`Attempted to set thickness to ${setTo}, but lowest available option is ${minThicknessOption}`);
      setTo = minThicknessOption;
    } else if (setTo > maxThicknessOption) {
      console.warn(`Attempted to set thickness to ${setTo}, but highest available option is ${maxThicknessOption}`);
      setTo = maxThicknessOption;
    };

    this._thickness = setTo;

    //Call setStoneSize to check for sizeExceptions
    this.setStoneSize(this._stoneSize);

    let meshes = await this._renderEngine.engine.services.render.getAllMeshesOfCurrentScene()
    meshes = meshes.filter((mesh) => (mesh.userData['ringSKU'] === ringModel.SKU));
    meshes.forEach((mesh:THREE.Mesh) => {
      const influence = this._calculateShapeKeyInfluence(thicknessOptions[0], thicknessOptions[thicknessOptions.length - 1], setTo);
      const targetIndex = mesh.userData.morphIndexes.thickness;
      if (targetIndex !== null) mesh.morphTargetInfluences[targetIndex] = influence;
    });
    this._renderEngine.updateLowestPointOfRing(this._ringModel.SKU, thicknessOptions[0], setTo);

    // console.log("setThickness() thicknessOptions", thicknessOptions);
    // console.log("setThickness() setTo", setTo);



    return Promise.resolve(this._thickness);
  };

  setStoneSize = (setTo:number):Promise<number> => {
    const { _ringModel: ringModel } = this;
    const morphOptions = ringModel.morphOptions;
    const stoneSizeOptions = morphOptions.stoneSize;
    const minStoneSizeOption = stoneSizeOptions.length > 0 ? Math.min(...stoneSizeOptions) : -Infinity;
    const maxStoneSizeOption = stoneSizeOptions.length > 0 ? Math.max(...stoneSizeOptions) : Infinity;

    const maxStoneSize = ringModel.getMaxStoneSize(this._width, this._thickness);
    if (setTo > maxStoneSize) {
      //console.log(`Attempted to set stone size to ${setTo}, but maximum allowed is ${maxStoneSize}`);
      const availableStoneSizes = stoneSizeOptions.filter(o => o <= maxStoneSize);
      setTo = Math.max(...availableStoneSizes);
    };

    if (setTo < minStoneSizeOption) {
      console.warn(`Attempted to set stoneSize to ${setTo}, but lowest available option is ${minStoneSizeOption}`);
      setTo = minStoneSizeOption;
    } else if (setTo > maxStoneSizeOption) {
      console.warn(`Attempted to set stoneSize to ${setTo}, but highest available option is ${maxStoneSizeOption}`);
      setTo = maxStoneSizeOption;
    };

    this._stoneSize = setTo;

    return new Promise((resolve, reject) => {
      this._renderEngine.engine.services.render.getAllMeshesOfCurrentScene()
      .then((meshes:THREE.Mesh[]) => {
        meshes = meshes.filter((mesh) => (mesh.userData['ringSKU'] === ringModel.SKU));
        meshes.forEach((mesh:THREE.Mesh) => {
          const influence = this._calculateShapeKeyInfluence(stoneSizeOptions[0], stoneSizeOptions[stoneSizeOptions.length - 1], setTo);
          const targetIndex = mesh.userData.morphIndexes.stoneSize;
          if (targetIndex !== null) mesh.morphTargetInfluences[targetIndex] = influence;
        });
        resolve(this._stoneSize);
      });
    });
  };// /_setRingStoneSize()

  updateMetalGroup = (newMetalGroupState:IMetalGroup, options:IUpdateMetalGroupOptions = {}) => {
    const { _ringModel: ringModel, _metalGroups: metalGroups } = this;
    const currentMetalGroupState = metalGroups.find((rmg) => rmg.name === newMetalGroupState.name);

    //'prevent'-actions are a special case. We only ever want to execute them using updateDisabledOptions()
    if (!options.ignoreExceptionActions) options.ignoreExceptionActions = [];
    if (!options.ignoreExceptionActions.includes('prevent')) options.ignoreExceptionActions.push('prevent');

    //This block needs to come before the material block messes with purity!
    if (options.forcePurityUpdate || (newMetalGroupState.purity !== currentMetalGroupState.purity)) {
      //console.log("Purity on MetalGroup changed!", newMetalGroupState.name, newMetalGroupState.purity);
      currentMetalGroupState.purity = newMetalGroupState.purity;
    };
    //--

    if (newMetalGroupState.material && (options.forceMaterialUpdate || (newMetalGroupState.material !== currentMetalGroupState.material))) {
      //console.log("Material on MetalGroup changed!", newMetalGroupState.name, newMetalGroupState.material);
      currentMetalGroupState.material = newMetalGroupState.material;

      let zone = ringModel.metalGroups.find((rmg) => rmg.name === newMetalGroupState.name);
      if(zone && zone.purityList && zone.purityList.length > 0){
        currentMetalGroupState.purityOptions = zone.purityList.filter(e => e.material === newMetalGroupState.material).map(e => e.purity);
      }
      else{
        currentMetalGroupState.purityOptions = RING_MATERIALS.find(rm => rm.name === newMetalGroupState.material).purities;
      }
      this._setDefaultPurity(currentMetalGroupState);

      ringModel.materialExceptions.forEach((exception) => {
        this._exceptionHandler.handleException(exception, newMetalGroupState.name, options.ignoreExceptionActions);
      });
    };

    if (newMetalGroupState.surface && (options.forceSurfaceUpdate || (newMetalGroupState.surface !== currentMetalGroupState.surface))) {
      //console.log("Surface on MetalGroup changed!", newMetalGroupState.name, newMetalGroupState.surface);
      currentMetalGroupState.surface = newMetalGroupState.surface;

      ringModel.surfaceExceptions.forEach((exception) => {
        this._exceptionHandler.handleException(exception, newMetalGroupState.name, options.ignoreExceptionActions);
      });
    };

    //Match purities of metal groups with the same material to purity of newly changed material
    this._matchPurities(currentMetalGroupState);

    metalGroups.forEach((metalGroup) => {
      this._updateMetalZones(metalGroup);
    });
  };// /updateMetalGroup()

  updateDisabledOptions = () => {
    const { _ringModel: ringModel, _metalGroups: metalGroups } = this;
    metalGroups.forEach((metalGroup) => {
      metalGroup.disabledMaterialOptions = [];
      metalGroup.disabledSurfaceOptions = [];
    });
    ringModel.materialExceptions.forEach((exception) => {
      this._exceptionHandler.handleException(exception, null, ['copy', 'message']);
    });
    ringModel.surfaceExceptions.forEach((exception) => {
      this._exceptionHandler.handleException(exception, null, ['copy', 'message']);
    });

    //console.log("updateDisabledOptions() finished!");
    metalGroups.forEach((mg) => {
      if (mg.disabledMaterialOptions.length > 0) {
        //console.log("Preventing material options:", mg.name, mg.disabledMaterialOptions);
      }
    });
    metalGroups.forEach((mg) => {
      if (mg.disabledSurfaceOptions.length > 0) {
        //console.log("Preventing surface options:", mg.name, mg.disabledSurfaceOptions);
      }
    });
  }// /updateDisabledOptions()

  /*
    TODO:?
    Maybe just keep track of current purity for each material separately from metalGroups and simply map to metalGroups when they change... ?
  */
  private _matchPurities = (changedMetalGroup:IMetalGroup) => {
    //console.log(changedMetalGroup, changedMetalGroup.purity);

    const goldMaterials = ['Yellow Gold', 'White Gold', 'Rose Gold'];
    let targetMetalGroups:IMetalGroup[] = [];

    if (goldMaterials.includes(changedMetalGroup.material)) {
      targetMetalGroups = this._metalGroups.filter((mg) => goldMaterials.includes(mg.material));
    } else {
      targetMetalGroups = this._metalGroups.filter((mg) => mg.material === changedMetalGroup.material);
    };

    for (let targetMetalGroup of targetMetalGroups) {
      targetMetalGroup.purity = changedMetalGroup.purity;
    };
  };// /_matchPurities()

  private _setDefaultPurity = (changedMetalGroup:IMetalGroup) => {
    /*
      Check if another metalGroup with the same material exists,
      if yes copy its purity to changedMetalGroup, otherwise fall back on default purity
    */

    //console.log("_setDefaultPurity() called", changedMetalGroup);

    if (!changedMetalGroup.purityOptions.includes(changedMetalGroup.purity)) {
      const goldMaterials = ['Yellow Gold', 'White Gold', 'Rose Gold'];
      let metalGroupWithSameMaterial:IMetalGroup = null;

      if (goldMaterials.includes(changedMetalGroup.material)) {
        metalGroupWithSameMaterial = this._metalGroups.find((mg) => (goldMaterials.includes(mg.material) && mg !== changedMetalGroup));
      } else {
        metalGroupWithSameMaterial = this._metalGroups.find((mg) => (mg.material === changedMetalGroup.material && mg !== changedMetalGroup));
      };

      if (metalGroupWithSameMaterial) {
        changedMetalGroup.purity = metalGroupWithSameMaterial.purity;
      } else {
        changedMetalGroup.purity = changedMetalGroup.purityOptions[0] || null;
      };
    };
  };// /_setDefaultPurity()

  //Update metalZones in a given metalGroup
  private _updateMetalZones = (metalGroup:IMetalGroupExt) => {
    const { _metalZones: metalZones } = this;
    const zonesToUpdate:{name:string, material:string}[] = [];

    metalGroup.materialZones.forEach((id) => {
      try {
        const metalZone = metalZones.find(mz => mz.id === id);

        metalZone.purity = metalGroup.purity;

        if (metalZone.material !== metalGroup.material) {
          metalZone.material = metalGroup.material;
          zonesToUpdate.push({ name: metalZone.name, material: ringMaterialHelper.getMaterialName(metalZone.material, metalZone.surface) });
        }
      } catch(error) {
        console.error("Error updating metalZone.material!", id, error);
      }
    });

    metalGroup.surfaceZones.forEach((id) => {
      try {
        const metalZone = metalZones.find(sz => sz.id === id);

        if (metalZone.surface !== metalGroup.surface) {
          metalZone.surface = metalGroup.surface;

          //Check if zones was already added to changed material to make sure we don't add any zones twice.
          const foundZone = zonesToUpdate.find(ztu => ztu.name === metalZone.name);
          if (foundZone) {
            foundZone.material = ringMaterialHelper.getMaterialName(metalZone.material, metalZone.surface);
          } else {
            zonesToUpdate.push({ name: metalZone.name, material: ringMaterialHelper.getMaterialName(metalZone.material, metalZone.surface) });
          };
        }
      } catch(error) {
        console.error("Error updating metalZone.surface!", id, error);
      }
    });

    this._updateModelMaterials(zonesToUpdate);
  };// /_updateMetalZones()

  public updateStoneGroup = (newStoneGroupState:IStoneGroup, options:IUpdateStoneGroupOptions = {}) => {
    const { _stoneGroups: stoneGroups } = this;
    const currentStoneGroupState = stoneGroups.find((rsg) => rsg.name === newStoneGroupState.name);

    if (newStoneGroupState.stone && (options.forceUpdate || (newStoneGroupState.stone !== currentStoneGroupState.stone))) {
      //console.log("Type on StoneGroup changed!", newStoneGroupState.name, newStoneGroupState.stone);
      currentStoneGroupState.stone = newStoneGroupState.stone;
      this._updateStoneZones(currentStoneGroupState);
    };
  };// /updateStoneGroup()

  //Update stoneZones in a given stoneGroup
  private _updateStoneZones = (stoneGroup:IStoneGroupExt) => {
    const { _stoneZones: stoneZones } = this;
    const zonesToUpdate:{name:string, material:string}[] = [];

    stoneGroup.zones.forEach((id) => {
      try {
        const stoneZone = stoneZones.find(sz => sz.id === id);

        if (stoneZone.stone !== stoneGroup.stone) {
          stoneZone.stone = stoneGroup.stone;
          zonesToUpdate.push({ name: stoneZone.name, material: `${stoneZone.stone}` });
        }
      } catch(error) {
        console.error("Error updating stoneZone!", id, error);
      }
    });

    this._updateModelMaterials(zonesToUpdate);
  };// /_updateStoneZones()

  private _updateModelMaterials = (zones:{name:string, material:string}[]) => {
    zones.forEach((zone) => {
      this._renderEngine.changeMaterial(zone.name, zone.material);
    })
  };// /_updateModelMaterials()

  public setEngravingText = (setTo:string) => {
    this._engravingText = setTo;
  };// /_changeEngravingFont()

  public setEngravingFont = (setTo:'Brush Script'|'Century Gothic'|'Snell Roundhand'|'Trajan'|'Dakota') => {
    this._engravingFont = setTo;
  };// /_changeEngravingFont()




  /* Calculate morphTarget influence corresponding to a given morphOption. 
   * Basically we just remap the morphOption's value within the range of morphOptions into a range from 0 to 1 
   */
  private _calculateShapeKeyInfluence = (min:number, max:number, value:number):number => {
    const range = max - min;
    const influence = ((value - min)/ range) || 0; //Return 0 instead of NaN if min === max
    return influence;
  };
}